import { useTranslation } from "react-i18next"; // TenantChatWidget.jsx - Floating AI chat bubble for the tenant portal
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RiRobot2Line, RiSendPlaneLine, RiLoader4Line, RiCloseLine } from 'react-icons/ri';
import api from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

const SUGGESTIONS = [
"What do I owe this month?",
"When is my next payment?",
"Did my last payment go through?",
"When does my lease end?"];


const TenantChatWidget = () => {const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
  { role: 'ai', text: "Hi! I'm your property assistant. Ask me about your account, rent, or lease." }]
  );
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  const send = async (text) => {
    const msg = text || message.trim();
    if (!msg) return;
    setMessages((prev) => [...prev, { role: 'user', text: msg }]);
    setMessage('');
    setLoading(true);
    try {
      const res = await api.post('/ai/tenant-chat', { message: msg });
      setMessages((prev) => [...prev, { role: 'ai', text: res.data.data.reply }]);
    } catch {
      setMessages((prev) => [...prev, { role: 'ai', text: "I'm having trouble connecting right now. Please try again shortly." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
            {/* Floating bubble */}
            <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 end-6 z-50 w-13 h-13 p-3.5 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg transition-all"
        aria-label="Open AI assistant">
        
                <AnimatePresence mode="wait">
                    {open ?
          <motion.span key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                            <RiCloseLine size={22} />
                        </motion.span> :

          <motion.span key="robot" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                            <RiRobot2Line size={22} />
                        </motion.span>
          }
                </AnimatePresence>
            </button>

            {/* Chat panel */}
            <AnimatePresence>
                {open &&
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={`fixed bottom-24 end-6 z-50 w-80 rounded-2xl shadow-2xl overflow-hidden flex flex-col ${isDarkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-gray-200'}`}>
          
                        {/* Header */}
                        <div className="flex items-center gap-2.5 px-4 py-3 bg-red-600 text-white">
                            <RiRobot2Line size={18} />
                            <div>
                                <p className="text-sm font-semibold leading-none">{t("property_assistant")}</p>
                                <p className="text-xs opacity-75 mt-0.5">{t("ask_about_your_account")}</p>
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 h-64 overflow-y-auto p-3 space-y-2.5">
                            {messages.map((m, i) =>
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
              m.role === 'user' ?
              'bg-red-600 text-white rounded-br-sm' :
              isDarkMode ? 'bg-zinc-700 text-zinc-100 rounded-bl-sm' : 'bg-gray-100 text-gray-800 rounded-bl-sm'}`
              }>
                                        {m.text}
                                    </div>
                                </div>
            )}
                            {loading &&
            <div className="flex justify-start">
                                    <div className={`px-3 py-2 rounded-xl rounded-bl-sm ${isDarkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}>
                                        <RiLoader4Line size={14} className="animate-spin text-red-400" />
                                    </div>
                                </div>
            }
                            <div ref={bottomRef} />
                        </div>

                        {/* Quick suggestions */}
                        <div className={`px-3 py-1.5 border-t flex flex-wrap gap-1 ${isDarkMode ? 'border-zinc-700' : 'border-gray-100'}`}>
                            {SUGGESTIONS.map((s) =>
            <button
              key={s}
              onClick={() => send(s)}
              className={`text-xs px-2 py-1 rounded-full transition-colors ${isDarkMode ? 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              
                                    {s}
                                </button>
            )}
                        </div>

                        {/* Input */}
                        <div className={`flex items-center gap-2 px-3 py-2.5 border-t ${isDarkMode ? 'border-zinc-700' : 'border-gray-100'}`}>
                            <input
              ref={inputRef}
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !loading && send()}
              placeholder="Type a message..."
              className={`flex-1 text-sm px-3 py-1.5 rounded-lg border outline-none ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white placeholder-zinc-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'}`} />
            
                            <button
              onClick={() => send()}
              disabled={loading || !message.trim()}
              className={`p-1.5 rounded-lg transition-colors ${loading || !message.trim() ? 'text-zinc-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'}`}>
              
                                <RiSendPlaneLine size={15} />
                            </button>
                        </div>
                    </motion.div>
        }
            </AnimatePresence>
        </>);

};

export default TenantChatWidget;